import React, { useState, useEffect, useMemo } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

import { API, detectTerritoire, detectPlan, ageInDays, csvEscape, anciennetéBucket } from './gcaConstants';

export function useGestionCoopers() {
  const { getAuthHeader } = useAuth();
  const [coopers, setCoopers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCooper, setSelectedCooper] = useState(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [territoireFilter, setTerritoireFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [ancienneteFilter, setAncienneteFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Checklist
  const [checklist, setChecklist] = useState({
    documents_verifies: false,
    siret_valide: false,
    statut_juridique_conforme: false,
    capacite_gestion_verifiee: false,
  });

  useEffect(() => { fetchCoopers(); }, []);

  const fetchCoopers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/coopers`, { headers: getAuthHeader() });
      setCoopers(res.data?.coopers || res.data || []);
    } catch {
      toast.error('Erreur lors du chargement des COOPER\'S');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetailModal = (cooper) => {
    setSelectedCooper(cooper);
    setOpenDetailModal(true);
    setChecklist({
      documents_verifies: false,
      siret_valide: false,
      statut_juridique_conforme: false,
      capacite_gestion_verifiee: false,
    });
  };

  const handleOpenActionDialog = (cooper, action) => {
    setSelectedCooper(cooper);
    setActionType(action);
    setActionReason('');
    setOpenActionDialog(true);
  };

  const handleAction = async () => {
    if (['suspend', 'reject'].includes(actionType) && !actionReason.trim()) {
      toast.error('Veuillez indiquer la raison');
      return;
    }
    if (actionType === 'validate') {
      const allChecked = Object.values(checklist).every(v => v === true);
      if (!allChecked) {
        toast.error('Veuillez cocher tous les points de la checklist');
        return;
      }
    }
    setProcessing(true);
    try {
      let endpoint = '';
      let data = {};
      switch (actionType) {
        case 'validate':
          endpoint = `${API}/coopers/${selectedCooper.id}/validate`;
          data = { is_validated: true }; break;
        case 'suspend':
          endpoint = `${API}/coopers/${selectedCooper.id}/status`;
          data = { statut: 'suspendu', reason: actionReason }; break;
        case 'activate':
          endpoint = `${API}/coopers/${selectedCooper.id}/status`;
          data = { statut: 'actif' }; break;
        case 'reject':
          endpoint = `${API}/coopers/${selectedCooper.id}/reject`;
          data = { reason: actionReason }; break;
      }
      await axios.put(endpoint, data, { headers: getAuthHeader() });
      const messages = {
        validate: 'COOPER\'S validé avec succès',
        suspend: 'COOPER\'S suspendu',
        activate: 'COOPER\'S réactivé',
        reject: 'COOPER\'S rejeté',
      };
      toast.success(messages[actionType]);
      setOpenActionDialog(false);
      setOpenDetailModal(false);
      fetchCoopers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'action');
    } finally {
      setProcessing(false);
    }
  };

  // Score de performance
  const calculatePerformanceScore = (cooper) => {
    let score = 0;
    const m = cooper.membres_assignes || 0;
    const d = cooper.dossiers_traites || 0;
    const t = cooper.taux_reussite || 0;
    if (m > 10) score += 30; else if (m > 5) score += 20; else if (m > 0) score += 10;
    if (d > 20) score += 40; else if (d > 10) score += 25; else if (d > 0) score += 10;
    score += Math.min(t, 30);
    return Math.min(score, 100);
  };

  // ============ Filtrage & options ============

  // Options dynamiques (territoires & plans présents dans les data)
  const availableTerritoires = useMemo(() => {
    const set = new Set(coopers.map(detectTerritoire).filter(Boolean));
    return Array.from(set).sort();
  }, [coopers]);

  const availablePlans = useMemo(() => {
    const set = new Set(coopers.map(detectPlan).filter(Boolean));
    return Array.from(set).sort();
  }, [coopers]);

  const filteredCoopers = useMemo(() => {
    let arr = [...coopers];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      arr = arr.filter(c =>
        c.entreprise?.toLowerCase().includes(q) ||
        c.siret?.includes(searchQuery) ||
        c.user?.email?.toLowerCase().includes(q) ||
        c.user?.nom?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      arr = arr.filter(c => {
        if (statusFilter === 'validated') return c.is_validated && c.is_active;
        if (statusFilter === 'pending') return !c.is_validated;
        if (statusFilter === 'suspended') return !c.is_active;
        return true;
      });
    }
    if (territoireFilter !== 'all') {
      arr = arr.filter(c => detectTerritoire(c) === territoireFilter);
    }
    if (planFilter !== 'all') {
      arr = arr.filter(c => detectPlan(c) === planFilter);
    }
    if (ancienneteFilter !== 'all') {
      arr = arr.filter(c => anciennetéBucket(ageInDays(c.created_at)) === ancienneteFilter);
    }
    arr.sort((a, b) => {
      const dA = new Date(a.created_at || 0).getTime();
      const dB = new Date(b.created_at || 0).getTime();
      return sortOrder === 'desc' ? dB - dA : dA - dB;
    });
    return arr;
  }, [coopers, searchQuery, statusFilter, territoireFilter, planFilter, ancienneteFilter, sortOrder]);

  const resetFilters = () => {
    setSearchQuery(''); setStatusFilter('all');
    setTerritoireFilter('all'); setPlanFilter('all'); setAncienneteFilter('all');
  };
  const hasActiveFilter = searchQuery || statusFilter !== 'all' || territoireFilter !== 'all' || planFilter !== 'all' || ancienneteFilter !== 'all';

  // ============ Exports ============

  const exportCSV = () => {
    const headers = [
      'Entreprise', 'SIRET', 'Réf. ESS', 'Statut', 'Email', 'Téléphone',
      'Territoire', 'Plan', 'Membres', 'Dossiers', 'Taux réussite (%)',
      'Performance (%)', 'Date inscription',
    ];
    const rows = filteredCoopers.map(c => [
      c.entreprise || '',
      c.siret || '',
      c.ref_ess || '',
      !c.is_active ? 'Suspendu' : !c.is_validated ? 'En attente' : 'Validé',
      c.user?.email || '',
      c.user?.telephone || '',
      detectTerritoire(c),
      detectPlan(c),
      c.membres_assignes || 0,
      c.dossiers_traites || 0,
      c.taux_reussite || 0,
      calculatePerformanceScore(c),
      c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(csvEscape).join(';')).join('\n');
    // BOM UTF-8 pour Excel
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `coopers_oscop_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filteredCoopers.length} COOPER'S exportés en CSV`);
  };

  const exportPDF = () => {
    // Génération PDF via window.print() sur une fenêtre dédiée — léger & robuste.
    const win = window.open('', '_blank', 'width=1024,height=720');
    if (!win) {
      toast.error('Veuillez autoriser les pop-ups pour exporter en PDF');
      return;
    }
    const rows = filteredCoopers.map(c => `
      <tr>
        <td>${(c.entreprise || '').replace(/[<>]/g, '')}</td>
        <td class="mono">${c.siret || '—'}</td>
        <td>${detectTerritoire(c)}</td>
        <td>${detectPlan(c)}</td>
        <td>${c.membres_assignes || 0}</td>
        <td>${c.dossiers_traites || 0}</td>
        <td>${c.taux_reussite || 0}%</td>
        <td>${calculatePerformanceScore(c)}%</td>
        <td>${!c.is_active ? 'Suspendu' : !c.is_validated ? 'En attente' : 'Validé'}</td>
      </tr>`).join('');
    win.document.write(`
      <!doctype html><html lang="fr"><head><meta charset="utf-8"/>
      <title>Registre COOPER'S O'SCOP — ${new Date().toLocaleDateString('fr-FR')}</title>
      <style>
        @page { size: A4 landscape; margin: 18mm 14mm; }
        * { box-sizing: border-box; }
        body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; color: #0a3d62; margin: 0; }
        .header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #DEA900; padding-bottom: 12px; margin-bottom: 18px; }
        .brand { font-size: 11px; letter-spacing: 0.2em; color: #DEA900; font-weight: 700; }
        h1 { margin: 4px 0 0; font-size: 22px; }
        .meta { font-size: 11px; color: #475569; text-align: right; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th { background: #0a3d62; color: #fff; padding: 8px 10px; text-align: left; font-weight: 600; }
        td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; }
        tr:nth-child(even) td { background: #f8fafc; }
        .mono { font-family: ui-monospace, Menlo, monospace; font-size: 10px; }
        .footer { margin-top: 18px; font-size: 10px; color: #64748b; border-top: 1px solid #e2e8f0; padding-top: 10px; display: flex; justify-content: space-between; }
        @media print { .no-print { display: none; } }
      </style></head>
      <body>
        <div class="header">
          <div>
            <div class="brand">O'SCOP OUTREMER · REGISTRE COOPER'S</div>
            <h1>Liste des COOPER'S — ${filteredCoopers.length} entrée${filteredCoopers.length > 1 ? 's' : ''}</h1>
          </div>
          <div class="meta">
            Édité le ${new Date().toLocaleString('fr-FR')}<br/>
            ${hasActiveFilter ? 'Avec filtres actifs' : 'Vue complète'}
          </div>
        </div>
        <table>
          <thead><tr>
            <th>Entreprise</th><th>SIRET</th><th>Territoire</th><th>Plan</th>
            <th>Membres</th><th>Dossiers</th><th>Réussite</th><th>Perf.</th><th>Statut</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="footer">
          <span>Document confidentiel — usage interne O'SCOP Outremer</span>
          <span>Page 1</span>
        </div>
        <div class="no-print" style="margin-top:24px;text-align:center;">
          <button onclick="window.print()" style="padding:10px 24px;background:#DEA900;color:#0a3d62;border:0;border-radius:8px;font-weight:600;cursor:pointer;">Imprimer / Enregistrer en PDF</button>
        </div>
        <script>setTimeout(() => window.print(), 400);</script>
      </body></html>`);
    win.document.close();
    toast.success(`PDF en cours de génération (${filteredCoopers.length} COOPER'S)`);
  };

  // ============ RENDER ============

  return { actionReason, actionType, ancienneteFilter, availablePlans, availableTerritoires, calculatePerformanceScore, checklist, coopers, exportCSV, exportPDF, fetchCoopers, filteredCoopers, getAuthHeader, handleAction, handleOpenActionDialog, handleOpenDetailModal, hasActiveFilter, loading, openActionDialog, openDetailModal, planFilter, processing, resetFilters, searchQuery, selectedCooper, setActionReason, setActionType, setAncienneteFilter, setChecklist, setCoopers, setLoading, setOpenActionDialog, setOpenDetailModal, setPlanFilter, setProcessing, setSearchQuery, setSelectedCooper, setShowAdvanced, setSortOrder, setStatusFilter, setTerritoireFilter, showAdvanced, sortOrder, statusFilter, territoireFilter };
}
